import { NgClass } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { AppSettings } from "app/modules/shared/app.settings";
import { AccessProviderDirective } from "app/modules/shared/directives/access-provider.directive";
import { ConfirmationService, MenuItem, Message, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { AppIcons } from "../../../shared/app.icons";
import { CommonBindingDataService } from "../../../shared/services/common-binding-data.service";
import { StorageService } from "../../../shared/services/storage.service";
import { UserCommonService } from "../../../shared/services/user-common.service";
import { SettingsGeneralSettingsComponent } from "./administration/settings-general-settings/settings-general-settings.component";
import { SettingsSecurityComponent } from "./administration/settings-security/settings-security.component";
import { SettingsTimeSettingsComponent } from "./administration/settings-time-settings/settings-time-settings.component";
import { SettingsUserComponent } from "./authentication/settings-user/settings-user.component";
import { SettingsUserProfileComponent } from "./settings-user-profile/settings-user-profile.component";
import { ConfigService } from "app/modules/shared/services/config.service";

@Component({
  selector: "app-settings",
  standalone: true,
  imports: [
    RouterModule,
    BreadcrumbModule,
    TranslateModule,
    NgClass,
    AccessProviderDirective,
    SettingsUserProfileComponent,
    SettingsGeneralSettingsComponent,
    SettingsTimeSettingsComponent,
    SettingsSecurityComponent,
    SettingsUserComponent,
  ],
  templateUrl: "./settings.component.html",
  styleUrl: "./settings.component.scss",
})
export class SettingsComponent implements OnInit, OnDestroy {
  miIcons = AppIcons;
  routePath: MenuItem[] = [];
  message: Message[] = [];
  activeSection: string = 'userProfile';
  listColumns: any[] = [];
  profileData: any;
  selectedEvent: any;
  logoutSession: any;
  hideAdminSection: boolean = false;
  sectionHandlers = {
    userProfile: "userProfile",
    notificationSettings: "notificationSettings",
    loginPassword: "loginPassword",
    generalManagement: 'generalManagement',
    orderStatusRename: 'orderStatusRename'
  };
  private routeSubscription: Subscription;

  constructor(
    private cs: CommonBindingDataService,
    private confirmationService: ConfirmationService,
    private userCommandService: UserCommonService,
    private storageService: StorageService,
    private router: Router,
    private messageService: MessageService,
    public configService: ConfigService,
  ) { }

  ngOnInit() {
    this.routeSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveSection();
    });
    this.setBreadcrumb();
    const activeSession = localStorage.getItem(AppSettings.FIELDS.SECTION_LABEL);
    if (activeSession) {
      this.setActiveSection(activeSession);
      const label = this.getSectionLabel(activeSession);
      if (label) {
        this.updateBreadcrumb(label);
      }
    }
    const isSiteUser = this.configService.isSiteUser();
    const isReviewer = this.configService.isReviewer();
    const isSponsor = this.configService.isSponsor();
    this.hideAdminSection = isSiteUser || isReviewer || isSponsor;
  }

  private getSectionLabel(section: string): string {
    switch (section) {
      case this.sectionHandlers.userProfile:
        return "User Profile";
      case this.sectionHandlers.notificationSettings:
        return "Notification Settings";
      case this.sectionHandlers.loginPassword:
        return "Change Password";
      case this.sectionHandlers.generalManagement:
        return "General Order Management";
      default:
        return "";
    }
  }

  updateActiveSection() {
    const currentUrl = this.router.url;

    if (currentUrl.includes('user-profile')) {
      this.activeSection = 'userProfile';
    } else if (currentUrl.includes('login-and-password')) {
      this.activeSection = this.sectionHandlers.loginPassword;
    } else if (currentUrl.includes('general-order-management')) {
      this.activeSection = this.sectionHandlers.generalManagement;
    }
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  private setBreadcrumb() {
    this.routePath = [
      { label: this.cs.getLabel("settings.lbl_settings") },
      {
        label: this.cs.getLabel("settings.lbl_user_profile"),
        styleClass: "breadcrumb-child forward-slash breadcrumb-text",
        style: { display: "flex", top: "2px", position: "relative" },
      },
    ];
  }

  updateBreadcrumb(label: string) {
    this.routePath = [
      { label: this.cs.getLabel("settings.lbl_settings") },
      {
        label,
        styleClass: "breadcrumb-child forward-slash breadcrumb-text",
        style: { display: "flex", top: "2px", position: "relative" },
      },
    ];
  }

  doLogOut() {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.cs.getLabel("msg_logout"),
      header: this.cs.getLabel("header_logout_confirmation"),
      icon: "",
      acceptButtonStyleClass: "p-button-danger",
      rejectButtonStyleClass: "cursor-pointer  p-button default-text-light-black-color  bg-white",
      acceptIcon: "none",
      rejectIcon: "none",
      acceptLabel: 'Logout',
      rejectLabel: this.cs.getLabel('events.lbl_cancel'),
      accept: () => {
        this.logout();
      },
    });
  }

  logout() {
    this.userCommandService.signOut().subscribe(
      () => {
        if (this.logoutSession) {
          clearTimeout(this.logoutSession);
        }

        this.listColumns = [];
        this.message = [];
        this.profileData = null;
        this.selectedEvent = null;

        this.storageService.removeAll();
        this.router.navigate(['auth/login']);
        window.location.reload();
        this.messageService.add({ severity: 'success', summary: 'Logout Successful', detail: this.cs.getLabel('logout_success') });
      },
      (error) => {
        this.errorMessage(error);
      }
    );
  }

  errorMessage(error) {
    error = JSON.parse(error);
    this.message = [];
    this.message.push({
      severity: "error",
      summary: "error",
      detail: error.general[0].message,
    });
  }

  setActiveSection(section: string) {
    const sectionLabel = this.sectionHandlers[section];
    if (sectionLabel) {
      localStorage.setItem(AppSettings.FIELDS.SECTION_LABEL, sectionLabel);
      this.updateBreadcrumb(sectionLabel);
      this.activeSection = sectionLabel;
    }
  }
}
